// Implements: F-UIX-008, F-UIX-007
/**
 * The code field, which exists only once the server has revealed that it wants one.
 *
 * Mounting IS the reveal: the container renders this component when the state
 * machine reaches `secondFactorRequired` or `secondFactorInvalid`, so the
 * mount effect below is exactly "move the focus to the field that just
 * appeared". That is not `autoFocus`, which `jsx-a11y/no-autofocus` forbids
 * for the good reason that stealing the focus on page load takes the choice
 * away from whoever is reading; here the person just pressed a button and the
 * field is the answer to it.
 *
 * `inputMode="numeric"` gets the digit keyboard on a phone and
 * `autoComplete="one-time-code"` lets the platform offer the code it received,
 * which is the difference between typing six digits and tapping once.
 */

import { useEffect, useRef } from "react";
import type { ReactElement } from "react";

import { useTranslation } from "../../../shared/i18n";
import { Input } from "../../../shared/ui/atoms/Input";
import { Field, describedBy } from "../../../shared/ui/molecules/Field";
import styles from "./SecondFactorField.module.css";

/** The identifier the label points at. */
export const SECOND_FACTOR_FIELD_ID = "login-totp";

export interface SecondFactorFieldProps {
  readonly value: string;
  readonly onChange: (value: string) => void;
  /** The code that was sent came back rejected. */
  readonly invalid?: boolean | undefined;
  readonly disabled?: boolean | undefined;
}

export function SecondFactorField({
  value,
  onChange,
  invalid = false,
  disabled = false,
}: SecondFactorFieldProps): ReactElement {
  const { t } = useTranslation();
  const field = useRef<HTMLInputElement>(null);

  useEffect(() => {
    field.current?.focus();
  }, []);

  const hint = t("auth.secondFactor.hint");
  const error = invalid ? t("auth.secondFactor.invalid") : undefined;

  return (
    <div className={styles.wrapper}>
      {invalid ? null : (
        // Announced once, when the field appears: a screen reader that hears
        // the field but not the reason has been told half of what happened.
        <p className={styles.announcement} role="status">
          {t("auth.secondFactor.required")}
        </p>
      )}
      <Field
        id={SECOND_FACTOR_FIELD_ID}
        label={t("auth.secondFactor.label")}
        hint={hint}
        error={error}
        required
      >
        <Input
          id={SECOND_FACTOR_FIELD_ID}
          value={value}
          onChange={onChange}
          inputMode="numeric"
          autoComplete="one-time-code"
          invalid={invalid}
          describedBy={describedBy(SECOND_FACTOR_FIELD_ID, { hint, error })}
          disabled={disabled}
          inputRef={field}
        />
      </Field>
    </div>
  );
}
